// /fair — business-card chat for the CPHI 2026 trade fair (mobile, full screen).
//
// Spec: docs/specs/messe-lead-capture-SPEC.md, design: messe-lead-capture-mockup.html
// (states ①–④ + the "couldn't read the card" error). Exactly two inputs: a card
// photo (→ lead, created by bitop-email-service via /api/fair/cards) and the note
// slot (→ note on the user's MOST RECENT card, also while it is still being read).
//
// Login: deep link /fair#k=<token>. The token is stored like a normal Atlas login
// (localStorage "sb_token"); the hash stays in the URL on purpose so the
// home-screen bookmark carries it (a hash is never sent to a server).
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, postForm, setToken } from "../api";

// ── API shapes (app/routers/fair.py) ─────────────────────────────────────────
interface CardDto {
  client_id: string;
  status: "pending" | "created" | "failed";
  lead_id: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  salesforce_url: string | null;
  error: string | null;
  created_at: string;
}
interface NoteDto {
  client_id: string;
  card_client_id: string;
  text: string;
  status: "pending" | "sending" | "sent" | "failed";
  created_at: string;
}
interface TodayDto {
  cards: CardDto[];
  notes: NoteDto[];
}

// ── Local chat model ─────────────────────────────────────────────────────────
type CardState = "reading" | "created" | "failed";
interface Card {
  clientId: string;
  state: CardState;
  at: Date;
  blob: Blob | null; // kept for "Try again" (null after a reload)
  blobUrl: string | null;
  name: string;
  company: string;
  sfUrl: string | null;
}
type NoteState = "pending" | "sent" | "failed";
interface Note {
  clientId: string;
  cardClientId: string;
  text: string;
  state: NoteState;
  at: Date;
}
type Entry =
  | { key: string; kind: "photo"; card: string }
  | { key: string; kind: "card-reply"; card: string }
  | { key: string; kind: "note"; note: string }
  | { key: string; kind: "note-reply"; note: string };

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
const hhmm = (d: Date) =>
  d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

function cardFromDto(d: CardDto, prev?: Card): Card {
  const name = [d.first_name, d.last_name].filter(Boolean).join(" ").trim();
  return {
    clientId: d.client_id,
    state: d.status === "created" ? "created" : d.status === "failed" ? "failed" : "reading",
    at: prev?.at ?? new Date(d.created_at),
    blob: prev?.blob ?? null,
    blobUrl: prev?.blobUrl ?? null,
    name: name || "Unnamed contact",
    company: d.company || "",
    sfUrl: d.salesforce_url,
  };
}
const noteState = (s: NoteDto["status"]): NoteState =>
  s === "sent" ? "sent" : s === "failed" ? "failed" : "pending";

// Downscale to ~1600 px (long side) JPEG before upload — keeps it ~300 KB on
// mobile data. Falls back to the original file if the browser cannot decode it.
async function downscale(file: File): Promise<Blob> {
  const MAX = 1600;
  try {
    let src: CanvasImageSource;
    let w: number;
    let h: number;
    if ("createImageBitmap" in window) {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
      src = bmp;
      w = bmp.width;
      h = bmp.height;
    } else {
      const img = new Image();
      const url = URL.createObjectURL(file);
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error("decode"));
        img.src = url;
      });
      URL.revokeObjectURL(url);
      src = img;
      w = img.naturalWidth;
      h = img.naturalHeight;
    }
    const scale = Math.min(1, MAX / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext("2d")!.drawImage(src, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    return blob ?? file;
  } catch {
    return file;
  }
}

// Swap in the fair manifest + iOS home-screen meta while /fair is mounted.
function useFairHomeScreen() {
  useEffect(() => {
    const head = document.head;
    const restore: Array<() => void> = [];
    const setAttr = (selector: string, create: () => HTMLElement, attr: string, value: string) => {
      let el = head.querySelector<HTMLElement>(selector);
      if (!el) {
        el = create();
        head.appendChild(el);
        const added = el;
        restore.push(() => added.remove());
      } else {
        const old = el.getAttribute(attr);
        const kept = el;
        restore.push(() => (old == null ? kept.removeAttribute(attr) : kept.setAttribute(attr, old)));
      }
      el.setAttribute(attr, value);
    };
    const link = (rel: string) => () => {
      const l = document.createElement("link");
      l.rel = rel;
      return l;
    };
    const meta = (name: string) => () => {
      const m = document.createElement("meta");
      m.name = name;
      return m;
    };
    setAttr('link[rel="manifest"]', link("manifest"), "href", "/fair.webmanifest");
    setAttr('link[rel="apple-touch-icon"]', link("apple-touch-icon"), "href", "/fair-apple-touch-icon.png");
    setAttr('meta[name="apple-mobile-web-app-capable"]', meta("apple-mobile-web-app-capable"), "content", "yes");
    setAttr('meta[name="apple-mobile-web-app-title"]', meta("apple-mobile-web-app-title"), "content", "CPHI Leads");
    const oldTitle = document.title;
    document.title = "CPHI Leads";
    restore.push(() => (document.title = oldTitle));
    return () => restore.reverse().forEach((f) => f());
  }, []);
}

// ── Icons ────────────────────────────────────────────────────────────────────
const CameraIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);
const GalleryIcon = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="9" cy="10" r="2" />
    <path d="M21 16l-5-5-8 8" />
  </svg>
);
const SendIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

// Spinner + no global focus ring on the text inputs (their border turns blue
// instead, as in the mockup; buttons keep the a11y ring).
const FAIR_CSS =
  "@keyframes fair-spin{to{transform:rotate(360deg)}} .fair-spin{animation:fair-spin .8s linear infinite}" +
  " .fair-app input:focus-visible{box-shadow:none;outline:none}";

// ── Access key screen ────────────────────────────────────────────────────────
function KeyScreen({ error, onSubmit }: { error: string | null; onSubmit: (k: string) => void }) {
  const [k, setK] = useState("");
  return (
    <div className="fair-app fixed inset-0 flex flex-col items-center justify-center bg-[#f8fafc] px-6 text-[#0f172a]"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <style>{FAIR_CSS}</style>
      <form className="w-full max-w-[360px]" onSubmit={(e) => { e.preventDefault(); if (k.trim()) onSubmit(k.trim()); }}>
        <div className="text-[20px] font-bold">CPHI 2026 · Leads</div>
        <label htmlFor="fair-key" className="mt-6 block text-[14px] text-[#64748b]">Enter your access key</label>
        <input id="fair-key" value={k} onChange={(e) => setK(e.target.value)} autoComplete="off"
          autoCapitalize="off" spellCheck={false}
          className="mt-2 w-full rounded-[14px] border border-[#e2e8f0] bg-white px-4 py-3 text-[16px] outline-none focus:border-[#1d4ed8]" />
        {error && <div className="mt-3 text-[14px] text-[#b91c1c]">{error}</div>}
        <button type="submit" disabled={!k.trim()}
          className="mt-4 h-[48px] w-full rounded-[14px] bg-[#1d4ed8] text-[16px] font-bold text-white disabled:bg-[#cbd5e1]">
          Continue
        </button>
      </form>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function FairPage() {
  useFairHomeScreen();
  const [auth, setAuth] = useState<"checking" | "ok" | "nokey" | "offline">("checking");
  const [keyError, setKeyError] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>(() => localStorage.getItem("sb_username") || "");

  const [cards, setCards] = useState<Record<string, Card>>({});
  const [notes, setNotes] = useState<Record<string, Note>>({});
  const [order, setOrder] = useState<string[]>([]); // card client ids, capture order
  const [entries, setEntries] = useState<Entry[]>([]);
  const [draft, setDraft] = useState("");
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef(cards);
  cardsRef.current = cards;

  // ── login ──
  const checkKey = useCallback(async () => {
    setAuth("checking");
    try {
      const me = await api.get<{ name: string }>("/auth/me");
      setUserName(me.name);
      localStorage.setItem("sb_username", me.name);
      setAuth("ok");
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setToken(null);
        setKeyError("This access key is not valid.");
        setAuth("nokey");
      } else {
        setAuth("offline");
      }
    }
  }, []);

  useEffect(() => {
    const k = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("k");
    if (k) setToken(k);
    if (!localStorage.getItem("sb_token")) {
      setAuth("nokey");
      return;
    }
    void checkKey();
  }, [checkKey]);

  const submitKey = (k: string) => {
    setToken(k);
    // Put it into the hash so "Add to Home Screen" bookmarks a working link.
    window.history.replaceState(null, "", `${window.location.pathname}#k=${encodeURIComponent(k)}`);
    setKeyError(null);
    void checkKey();
  };

  // ── rebuild from /api/fair/today ──
  const rebuild = useCallback(async () => {
    const t = await api.get<TodayDto>("/api/fair/today");
    const cs: Record<string, Card> = {};
    const ns: Record<string, Note> = {};
    type Ev = { at: number; entries: Entry[] };
    const evs: Ev[] = [];
    for (const d of t.cards) {
      cs[d.client_id] = cardFromDto(d, cardsRef.current[d.client_id]);
      evs.push({
        at: Date.parse(d.created_at),
        entries: [
          { key: `p-${d.client_id}`, kind: "photo", card: d.client_id },
          { key: `r-${d.client_id}`, kind: "card-reply", card: d.client_id },
        ],
      });
    }
    for (const n of t.notes) {
      ns[n.client_id] = {
        clientId: n.client_id, cardClientId: n.card_client_id, text: n.text,
        state: noteState(n.status), at: new Date(n.created_at),
      };
      const e: Entry[] = [{ key: `n-${n.client_id}`, kind: "note", note: n.client_id }];
      if (n.status === "sent" || n.status === "failed")
        e.push({ key: `a-${n.client_id}`, kind: "note-reply", note: n.client_id });
      evs.push({ at: Date.parse(n.created_at), entries: e });
    }
    evs.sort((a, b) => a.at - b.at);
    setCards(cs);
    setNotes(ns);
    setOrder(t.cards.map((c) => c.client_id));
    setEntries(evs.flatMap((e) => e.entries));
  }, []);

  useEffect(() => {
    if (auth === "ok") void rebuild().catch(() => undefined);
  }, [auth, rebuild]);

  // A card still "pending" after a reload is being read by an earlier request —
  // poll until it settles (no local request would ever report it).
  const hasOrphanReading = useMemo(
    () => Object.values(cards).some((c) => c.state === "reading" && !c.blob),
    [cards],
  );
  useEffect(() => {
    if (!hasOrphanReading) return;
    const id = window.setInterval(() => void rebuild().catch(() => undefined), 4000);
    return () => window.clearInterval(id);
  }, [hasOrphanReading, rebuild]);

  // ── auto-scroll ──
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, cards, notes]);

  // ── helpers ──
  const pushEntries = (...e: Entry[]) => setEntries((prev) => [...prev, ...e]);
  const moveToEnd = (key: string) =>
    setEntries((prev) => {
      const hit = prev.find((x) => x.key === key);
      return hit ? [...prev.filter((x) => x.key !== key), hit] : prev;
    });
  const patchCard = (id: string, p: Partial<Card>) =>
    setCards((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], ...p } } : prev));
  const patchNote = (id: string, p: Partial<Note>) =>
    setNotes((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], ...p } } : prev));

  // Card answered: show the reply after anything typed meanwhile, then the
  // acks of the notes the server just flushed (state ③: created → note added).
  const settleCard = async (clientId: string, dto: CardDto | null) => {
    if (dto) setCards((prev) => ({ ...prev, [clientId]: cardFromDto(dto, prev[clientId]) }));
    else patchCard(clientId, { state: "failed" });
    moveToEnd(`r-${clientId}`);
    if (dto?.status !== "created") return;
    try {
      const t = await api.get<TodayDto>("/api/fair/today");
      const flushed = t.notes.filter((n) => n.card_client_id === clientId && n.status !== "pending" && n.status !== "sending");
      setNotes((prev) => {
        const next = { ...prev };
        for (const n of flushed) if (next[n.client_id]) next[n.client_id] = { ...next[n.client_id], state: noteState(n.status) };
        return next;
      });
      setEntries((prev) => {
        const have = new Set(prev.map((e) => e.key));
        const add = flushed
          .filter((n) => !have.has(`a-${n.client_id}`) && have.has(`n-${n.client_id}`))
          .map((n): Entry => ({ key: `a-${n.client_id}`, kind: "note-reply", note: n.client_id }));
        return [...prev, ...add];
      });
    } catch {
      /* acks appear on the next reload */
    }
  };

  const uploadCard = async (clientId: string, blob: Blob) => {
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("image", blob, "business_card.jpg");
    try {
      const dto = await postForm<CardDto>("/api/fair/cards", form);
      await settleCard(clientId, dto);
    } catch {
      await settleCard(clientId, null);
    }
  };

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    const clientId = newId();
    const blob = await downscale(file);
    const card: Card = {
      clientId, state: "reading", at: new Date(), blob, blobUrl: URL.createObjectURL(blob),
      name: "", company: "", sfUrl: null,
    };
    setCards((prev) => ({ ...prev, [clientId]: card }));
    setOrder((prev) => [...prev, clientId]);
    pushEntries(
      { key: `p-${clientId}`, kind: "photo", card: clientId },
      { key: `r-${clientId}`, kind: "card-reply", card: clientId },
    );
    await uploadCard(clientId, blob);
  };

  const retryCard = (clientId: string) => {
    const c = cardsRef.current[clientId];
    if (!c?.blob) return;
    patchCard(clientId, { state: "reading" });
    void uploadCard(clientId, c.blob);
  };

  const postNote = async (note: Note) => {
    try {
      const dto = await api.post<NoteDto>("/api/fair/notes", {
        text: note.text, client_id: note.clientId, card_client_id: note.cardClientId,
      });
      const st = noteState(dto.status);
      patchNote(note.clientId, { state: st });
      if (st !== "pending") {
        setEntries((prev) => {
          const key = `a-${note.clientId}`;
          const rest = prev.filter((e) => e.key !== key);
          return [...rest, { key, kind: "note-reply", note: note.clientId }];
        });
      }
    } catch {
      patchNote(note.clientId, { state: "failed" });
      setEntries((prev) => {
        const key = `a-${note.clientId}`;
        if (prev.some((e) => e.key === key)) return prev;
        return [...prev, { key, kind: "note-reply", note: note.clientId }];
      });
    }
  };

  const latestCardId = order.length ? order[order.length - 1] : null;
  const latest = latestCardId ? cards[latestCardId] : undefined;

  const sendNote = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !latestCardId) return;
    const note: Note = {
      clientId: newId(), cardClientId: latestCardId, text, state: "pending", at: new Date(),
    };
    setNotes((prev) => ({ ...prev, [note.clientId]: note }));
    pushEntries({ key: `n-${note.clientId}`, kind: "note", note: note.clientId });
    setDraft("");
    void postNote(note);
  };

  const createdToday = Object.values(cards).filter((c) => c.state === "created").length;
  const firstName = (userName || "").split(/[\s@.]/)[0] || "there";

  if (auth === "nokey") return <KeyScreen error={keyError} onSubmit={submitKey} />;

  // ── render ──
  const fileInputs = (
    <>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { void onPhoto(e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { void onPhoto(e.target.files?.[0]); e.target.value = ""; }} />
    </>
  );

  const renderEntry = (e: Entry) => {
    if (e.kind === "photo") {
      const c = cards[e.card];
      if (!c) return null;
      return (
        <div key={e.key} className="max-w-[86%] self-end rounded-[16px_4px_16px_16px] bg-[#1d4ed8] p-[5px] text-white">
          {c.blobUrl ? (
            <img src={c.blobUrl} alt="Business card" className="block max-h-[150px] max-w-[220px] rounded-[10px] object-cover" />
          ) : (
            <div className="flex h-[60px] w-[150px] items-center justify-center gap-2 rounded-[10px] bg-white/15 text-[13px]">
              <CameraIcon size={18} /> Business card
            </div>
          )}
          <div className="mt-1 px-1 text-right text-[11px] text-[#c7d2fe]">{hhmm(c.at)}</div>
        </div>
      );
    }
    if (e.kind === "card-reply") {
      const c = cards[e.card];
      if (!c) return null;
      if (c.state === "reading")
        return (
          <div key={e.key} className="max-w-[86%] self-start rounded-[4px_16px_16px_16px] border border-[#e2e8f0] bg-white px-3 py-[10px] text-[14px]">
            <span className="fair-spin mr-2 inline-block h-[14px] w-[14px] rounded-full border-2 border-[#c7d2fe] border-t-[#1d4ed8] align-[-2px]" />
            Reading the card…
          </div>
        );
      if (c.state === "failed")
        return (
          <div key={e.key} className="max-w-[86%] self-start rounded-[4px_16px_16px_16px] border border-[#e2e8f0] bg-white px-3 py-[10px] text-[14px] leading-[1.4]">
            <div className="mb-[3px] text-[11px] font-bold tracking-[.02em] text-[#1d4ed8]">ATLAS</div>
            <div className="mb-[6px] rounded-[10px] border border-[#fecaca] bg-[#fef2f2] px-[10px] py-2 text-[12.5px] text-[#7f1d1d]">
              Couldn't read the card{c.blob ? "" : ` from ${hhmm(c.at)}`}.
            </div>
            {c.blob ? "Photo kept." : "Please retake the photo."}
            <div>
              {c.blob && (
                <button type="button" onClick={() => retryCard(c.clientId)}
                  className="mr-[6px] mt-2 rounded-[10px] border border-[#e2e8f0] bg-white px-[11px] py-[7px] text-[12.5px] font-semibold text-[#0f172a]">
                  Try again
                </button>
              )}
              <button type="button" onClick={() => cameraRef.current?.click()}
                className="mt-2 rounded-[10px] border border-[#e2e8f0] bg-white px-[11px] py-[7px] text-[12.5px] font-semibold text-[#0f172a]">
                Retake photo
              </button>
            </div>
          </div>
        );
      return (
        <div key={e.key} className="max-w-[86%] self-start rounded-[4px_16px_16px_16px] border border-[#e2e8f0] bg-white px-3 py-[10px] text-[14px] leading-[1.4]">
          <div className="mb-[3px] text-[11px] font-bold tracking-[.02em] text-[#1d4ed8]">ATLAS</div>
          <div className="mb-[2px] text-[13px] font-bold text-[#047857]">✓ Lead created</div>
          <div className="text-[15px] font-bold">{c.name}</div>
          {c.company && <div className="text-[13.5px] text-[#64748b]">{c.company}</div>}
          {c.sfUrl && (
            <a href={c.sfUrl} target="_blank" rel="noopener noreferrer"
              className="mt-[7px] inline-block text-[13.5px] font-semibold text-[#1d4ed8] no-underline">
              Open in Salesforce ↗
            </a>
          )}
        </div>
      );
    }
    if (e.kind === "note") {
      const n = notes[e.note];
      if (!n) return null;
      return (
        <div key={e.key} className="max-w-[86%] self-end whitespace-pre-wrap break-words rounded-[16px_4px_16px_16px] bg-[#1d4ed8] px-3 py-[9px] text-[14px] leading-[1.4] text-white">
          {n.text}
          <div className="mt-1 text-right text-[11px] text-[#c7d2fe]">{hhmm(n.at)}</div>
        </div>
      );
    }
    const n = notes[e.note];
    if (!n) return null;
    const target = cards[n.cardClientId];
    // Name the lead only when it is no longer the one right above (mockup ④).
    const showName = target && target.clientId !== latestCardId;
    if (n.state === "failed")
      return (
        <div key={e.key} className="max-w-[86%] self-start rounded-[4px_16px_16px_16px] border border-[#e2e8f0] bg-white px-[11px] py-2 text-[13px]">
          <div className="font-bold text-[#b91c1c]">Note not saved.</div>
          <button type="button" onClick={() => { patchNote(n.clientId, { state: "pending" }); void postNote(n); }}
            className="mt-[6px] rounded-[10px] border border-[#e2e8f0] bg-white px-[11px] py-[7px] text-[12.5px] font-semibold text-[#0f172a]">
            Try again
          </button>
        </div>
      );
    return (
      <div key={e.key} className="max-w-[86%] self-start rounded-[4px_16px_16px_16px] border border-[#e2e8f0] bg-white px-[11px] py-2 text-[13px] font-bold text-[#047857]">
        ✓ Note added{showName && target?.name ? ` · ${target.name}` : ""}
      </div>
    );
  };

  const targetLabel = !latest
    ? null
    : latest.state === "created"
      ? [latest.name, latest.company].filter(Boolean).join(" · ")
      : latest.state === "reading"
        ? "card being read"
        : `card from ${hhmm(latest.at)} (not read yet)`;

  return (
    <div className="fair-app fixed inset-0 flex flex-col bg-[#f8fafc] text-[#0f172a]"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, sans-serif' }}>
      <style>{FAIR_CSS}</style>
      {fileInputs}
      {/* app bar */}
      <header className="flex items-center gap-[10px] border-b border-[#e2e8f0] bg-white px-[14px] pb-[10px]"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 10px)" }}>
        <div className="min-w-0 flex-1">
          <b className="block text-[15px]">CPHI 2026 · Leads</b>
          <span className="block truncate text-[12px] text-[#64748b]">
            {auth === "offline" ? "Can't reach Atlas" : userName ? `Signed in as ${userName}` : "Signing in…"}
          </span>
        </div>
        {auth === "offline" ? (
          <button type="button" onClick={() => void checkKey()}
            className="rounded-full bg-[#eff6ff] px-[10px] py-[6px] text-[12px] font-semibold text-[#1d4ed8]">Retry</button>
        ) : (
          <span className="whitespace-nowrap rounded-full bg-[#eff6ff] px-[10px] py-[6px] text-[12px] font-semibold text-[#1d4ed8]">
            {createdToday} today
          </span>
        )}
      </header>

      {/* chat */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain px-3 pb-[6px] pt-3">
        <div className={`flex min-h-full flex-col gap-[10px] ${entries.length ? "justify-end" : "justify-start"}`}>
          {entries.length === 0 && (
            <>
              <span className="self-center rounded-full bg-[#e9eef5] px-[10px] py-[3px] text-[11px] text-[#64748b]">Today</span>
              <div className="max-w-[86%] self-start rounded-[4px_16px_16px_16px] border border-[#e2e8f0] bg-white px-3 py-[10px] text-[14px] leading-[1.4]">
                <div className="mb-[3px] text-[11px] font-bold tracking-[.02em] text-[#1d4ed8]">ATLAS</div>
                Hi {firstName} 👋 Snap a business card and I'll create the lead.
                <br />
                <br />
                Anything you type below is added as a note to <b>your last lead</b>.
                <div className="mt-1 text-[11px] text-[#64748b]">Tip: the 🎤 on your keyboard dictates notes.</div>
              </div>
            </>
          )}
          {entries.map(renderEntry)}
        </div>
      </div>

      {/* composer */}
      <div className="border-t border-[#e2e8f0] bg-white px-[10px] pt-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 10px)" }}>
        {!latest ? (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => cameraRef.current?.click()} disabled={auth !== "ok"}
              className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-[14px] bg-[#1d4ed8] text-[16px] font-bold text-white disabled:opacity-60">
              <CameraIcon /> Scan business card
            </button>
            <button type="button" onClick={() => galleryRef.current?.click()} disabled={auth !== "ok"}
              aria-label="Choose from photos" title="From photos"
              className="flex h-[52px] w-[52px] flex-none items-center justify-center rounded-[14px] bg-[#f1f5f9] text-[#334155] disabled:opacity-60">
              <GalleryIcon />
            </button>
          </div>
        ) : (
          <>
            <div className="mx-[2px] mb-[7px] flex min-w-0 items-center gap-[6px] text-[12px] text-[#64748b]">
              <span className="flex-none">Note →</span>
              <b className="truncate rounded-full bg-[#eff6ff] px-[9px] py-[3px] font-semibold text-[#1d4ed8]">{targetLabel}</b>
            </div>
            <form className="flex items-center gap-2" onSubmit={sendNote}>
              <button type="button" onClick={() => cameraRef.current?.click()} aria-label="Scan business card"
                className="flex h-[46px] w-[46px] flex-none items-center justify-center rounded-[14px] bg-[#1d4ed8] text-white">
                <CameraIcon />
              </button>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Take a note…"
                enterKeyHint="send" autoComplete="off"
                className="min-w-0 flex-1 rounded-[22px] border border-[#e2e8f0] bg-[#f8fafc] px-[14px] py-[10px] text-[16px] text-[#0f172a] outline-none placeholder:text-[#94a3b8] focus:border-[#1d4ed8] focus:bg-white" />
              <button type="submit" disabled={!draft.trim()} aria-label="Add note"
                className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-full bg-[#1d4ed8] text-white disabled:bg-[#cbd5e1]">
                <SendIcon />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
